/**
 * Guided Set-out — one-screen prism-lead wizard.
 *
 * Design intent:
 *   * The surveyor walks the pole. The total station is auto-tracking
 *     the prism. This component just watches the live scope state.
 *   * When the surveyor stops moving (velocity below threshold for
 *     ~500 ms) we auto-shoot the current position and advance the
 *     wizard state.
 *   * On the layout stages (C, D, E, …) we show **big directional
 *     arrows** rotated into the surveyor's frame (looking away from
 *     the station) and emit **beeps** at a rate that ramps from slow
 *     → fast → continuous as they close on the design point.
 *
 * State machine stages:
 *     plane   → capture 3+ plane shots, then Lock Plane
 *     anchor  → auto-shoot Point A (the design's first corner)
 *     orient  → auto-shoot Point B (defines A→B direction)
 *     target  → guide to each remaining corner in order (with beeps)
 *     complete
 *
 * The existing SetoutTool step-by-step UI is untouched — this
 * component is opt-in via the header toggle.
 */

import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { toast } from "sonner";
import { ArrowLeft, ArrowUp, ArrowDown, ArrowLeft as ArrLeft, ArrowRight, Check, X, Volume2, VolumeX } from "lucide-react";
import { station } from "../lib/api";

// Sub-mrad angular still threshold — matches ShootPanel's live tracker.
const STILL_RAD = 0.00035;
const STILL_MS = 500;

// -------- Beep engine ------------------------------------------------------

/**
 * Distance-scaled beeper using Web Audio. Call `setRate(ms)` with the
 * gap between beeps, or `setRate(0)` for a continuous tone. `setRate(null)`
 * silences it. Reasonable range 60 - 900 ms.
 */
function useBeeper(muted) {
  const ctxRef = useRef(null);
  const oscRef = useRef(null);
  const gainRef = useRef(null);
  const timerRef = useRef(null);
  const rateRef = useRef(null);

  const ensureCtx = useCallback(() => {
    if (!ctxRef.current) {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return null;
      ctxRef.current = new AC();
      gainRef.current = ctxRef.current.createGain();
      gainRef.current.gain.value = 0;
      gainRef.current.connect(ctxRef.current.destination);
      oscRef.current = ctxRef.current.createOscillator();
      oscRef.current.type = "sine";
      oscRef.current.frequency.value = 880;
      oscRef.current.connect(gainRef.current);
      oscRef.current.start();
    }
    return ctxRef.current;
  }, []);

  const beepOnce = useCallback(() => {
    const c = ctxRef.current, g = gainRef.current;
    if (!c || !g || muted) return;
    const now = c.currentTime;
    g.gain.cancelScheduledValues(now);
    g.gain.setValueAtTime(0.0001, now);
    g.gain.exponentialRampToValueAtTime(0.25, now + 0.01);
    g.gain.exponentialRampToValueAtTime(0.0001, now + 0.05);
  }, [muted]);

  const setRate = useCallback((rateMs) => {
    ensureCtx();
    rateRef.current = rateMs;
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    const g = gainRef.current;
    if (!g) return;
    if (rateMs === null || muted) { g.gain.value = 0; return; }
    if (rateMs === 0) { g.gain.value = 0.15; return; }   // continuous
    g.gain.value = 0;
    beepOnce();
    timerRef.current = setInterval(beepOnce, rateMs);
  }, [beepOnce, ensureCtx, muted]);

  useEffect(() => () => {
    if (timerRef.current) clearInterval(timerRef.current);
    try { oscRef.current && oscRef.current.stop(); } catch { /* silent */ }
    // Guard against StrictMode double-invoke closing an already-closed
    // AudioContext (which throws and produces a red error overlay).
    try {
      const c = ctxRef.current;
      if (c && c.state !== "closed") c.close();
    } catch { /* silent */ }
    ctxRef.current = null;
    oscRef.current = null;
    gainRef.current = null;
  }, []);

  useEffect(() => {
    if (muted && gainRef.current) gainRef.current.gain.value = 0;
  }, [muted]);

  return { setRate };
}

// Convert a live station state polled at ~5 Hz to a still/moving signal.
// Every call pushes ONE observation into history — deliberately not
// gated on hz/v change, otherwise a static sim / debounced source
// (identical readings poll after poll) would leave history empty and
// stillness would never register.
function useStillDetector(thresholdRad = STILL_RAD, dwellMs = STILL_MS) {
  const historyRef = useRef([]);
  const stillSinceRef = useRef(null);
  const [isStill, setStill] = useState(false);

  const observe = useCallback((hz, v) => {
    if (!Number.isFinite(hz) || !Number.isFinite(v)) return;
    const H = historyRef.current;
    H.push({ hz, v, t: Date.now() });
    if (H.length > 8) H.shift();
    const hzs = H.map((p) => p.hz), vs = H.map((p) => p.v);
    const hRange = Math.max(...hzs) - Math.min(...hzs);
    const vRange = Math.max(...vs) - Math.min(...vs);
    const still = H.length >= 4 && hRange < thresholdRad && vRange < thresholdRad;
    if (still) {
      if (stillSinceRef.current == null) stillSinceRef.current = Date.now();
      setStill(Date.now() - stillSinceRef.current >= dwellMs);
    } else {
      stillSinceRef.current = null;
      setStill(false);
    }
  }, [thresholdRad, dwellMs]);

  const reset = useCallback(() => { historyRef.current = []; stillSinceRef.current = null; setStill(false); }, []);
  return { isStill, observe, reset };
}

// -------- Arrow display ----------------------------------------------------

/**
 * Rotate a world-frame delta into the surveyor's frame.
 *
 *   The surveyor's "forward" is defined as the direction from the
 *   station toward the prism (they typically walk away from the
 *   station looking forward). "Right" is 90° clockwise from that
 *   viewed from above, and "left" the opposite.
 *
 *   dx_world, dy_world = target minus prism (world XY).
 *   px, py             = prism world XY.
 *   sx, sy             = station world XY.
 *
 *   Returns (forward_m, right_m) — positive forward = keep walking
 *   away from the station, positive right = step right.
 */
function toSurveyorFrame(dx, dy, px, py, sx, sy) {
  const fx = px - sx, fy = py - sy;
  const fL = Math.hypot(fx, fy) || 1;
  const fh_x = fx / fL, fh_y = fy / fL;
  const rh_x = fh_y, rh_y = -fh_x;    // 90° CW
  const forward = dx * fh_x + dy * fh_y;
  const right = dx * rh_x + dy * rh_y;
  return { forward, right };
}

const BigArrow = ({ dir, magnitudeMm, label }) => {
  const active = magnitudeMm > 5;
  const Icon = dir === "up" ? ArrowUp : dir === "down" ? ArrowDown : dir === "left" ? ArrLeft : ArrowRight;
  return (
    <div className={`flex flex-col items-center gap-1 rounded-md border p-4 ${active ? "border-[#00E1C7] bg-[#00E1C7]/10 text-[#00E1C7]" : "border-[#2A2E39] bg-[#0F1116] text-[#4A5054]"}`}>
      <Icon size={64} strokeWidth={active ? 3 : 1.5} />
      <div className="text-[9px] uppercase tracking-widest">{label}</div>
      <div className="font-mono text-lg font-bold">{magnitudeMm >= 1 ? `${magnitudeMm.toFixed(0)} mm` : "· on ·"}</div>
    </div>
  );
};

// -------- Main component ---------------------------------------------------

export default function SetoutGuided({ current, soApi, setCurrent, onExit, tolerance_mm = 5 }) {
  const [scope, setScope] = useState({});
  const [muted, setMuted] = useState(false);
  const [busy, setBusy] = useState(false);
  const { setRate } = useBeeper(muted);
  const { isStill, observe, reset: resetStill } = useStillDetector();

  // Poll live scope at 5 Hz. This is our source of truth for prism XY/Z
  // (with prism-lock on, station tracks it). Every poll — regardless of
  // whether hz/v changed — is fed to the still-detector, otherwise a
  // debounced source (or the cloud sim with static readings) would
  // never fill the history buffer.
  useEffect(() => {
    let cancel = false;
    let iv = setInterval(async () => {
      try {
        const s = await station.state();
        if (cancel) return;
        setScope(s || {});
        observe(s?.hz, s?.v);
      } catch { /* silent */ }
    }, 200);
    return () => { cancel = true; clearInterval(iv); };
  }, [observe]);

  // Wizard stage — derived from session state so we resume correctly
  // if the surveyor comes back to a partially-completed guided run.
  const stage = useMemo(() => {
    if (!current) return "plane";
    const nPlane = (current.plane_shots || []).length;
    if (nPlane < 3 && current.plane_mode !== "fitted") return "plane";
    if (!current.anchor_world) return "anchor";
    if (current.orient_hz == null) return "orient";
    const nextIdx = current.targets.findIndex((t, i) => i >= 2 && !t.marked);
    if (nextIdx < 0) return "complete";
    return { name: "target", idx: nextIdx };
  }, [current]);

  const stageName = typeof stage === "string" ? stage : stage.name;
  const activeTarget = typeof stage === "object" ? current?.targets[stage.idx] : null;

  // ----- Auto-shot on stop ------------------------------------------------
  // The plane / anchor / orient stages fire when the prism goes still.
  // The layout stages just guide + beep; the surveyor manually taps
  // Confirm because "on the design point" isn't a stop, it's an
  // aligned stop we want them to double-check.

  // `captured` — dedupe within a single still window.
  // `armed` — the wizard must first see the prism actually MOVE (hz or
  //   v change between polls) before the next still-detector trigger
  //   counts as a shot. A static scope-state (cloud sim, disconnected
  //   bridge, debounced source) will therefore never auto-fire, and
  //   even on real hardware the surveyor's first movement from wherever
  //   they were standing is what arms the auto-shoot.
  const [captured, setCaptured] = useState(false);
  const [armed, setArmed] = useState(false);
  const lastPollRef = useRef({ hz: null, v: null });
  useEffect(() => {
    setCaptured(false);
    setArmed(false);
    lastPollRef.current = { hz: null, v: null };
    resetStill();
  }, [stageName, resetStill]);

  // Arm on the first observed change in hz or v. Uses a tolerance
  // slightly bigger than a floating-point wobble so a truly static
  // scope-state can never falsely arm.
  useEffect(() => {
    const prev = lastPollRef.current;
    const hz = scope.hz, v = scope.v;
    if (Number.isFinite(hz) && Number.isFinite(v)) {
      if (prev.hz != null && !armed) {
        const dhz = Math.abs(hz - prev.hz);
        const dv = Math.abs(v - prev.v);
        if (dhz > 1e-6 || dv > 1e-6) setArmed(true);
      }
      lastPollRef.current = { hz, v };
    }
  }, [scope.hz, scope.v, armed]);

  useEffect(() => {
    if (!armed || !isStill || captured || busy) return;
    if (!Number.isFinite(scope.hz)) return;
    // Only these three stages auto-capture the shot.
    if (stageName !== "plane" && stageName !== "anchor" && stageName !== "orient") return;
    (async () => {
      setBusy(true); setCaptured(true);
      try {
        const m = await station.measureLocal();
        if (!Number.isFinite(m?.sd)) throw new Error("no distance");
        if (stageName === "plane") {
          const s = await soApi.addPlaneShot(current.id, { x: m.x, y: m.y, z: m.z, hz: m.hz, v: m.v, sd: m.sd });
          setCurrent(s);
          toast.success(`Plane shot ${s.plane_shots.length}${s.plane_rms_mm != null ? ` · RMS ${s.plane_rms_mm.toFixed(1)} mm` : ""}`);
        } else if (stageName === "anchor") {
          const s = await soApi.setAnchor(current.id, { x: m.x, y: m.y, z: m.z, hz: m.hz, v: m.v, sd: m.sd });
          setCurrent(s);
          toast.success("Point A anchored");
        } else if (stageName === "orient") {
          const s = await soApi.setOrientMeasure(current.id, { x: m.x, y: m.y, z: m.z, hz: m.hz, v: m.v, sd: m.sd });
          setCurrent(s);
          toast.success("Point B direction locked");
        }
      } catch (e) {
        toast.error(e?.response?.data?.detail || e?.message || "Auto-shot failed");
        setCaptured(false);
      } finally { setBusy(false); }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStill, stageName]);

  // ----- Target guidance + beep rate --------------------------------------
  const guide = useMemo(() => {
    if (!activeTarget || activeTarget.world_x == null) return null;
    // Live prism XY/Z on the /api/station/state payload are
    // `target_x/y/z` (with target_type=prism), NOT `x/y/z`. Station
    // coordinates are `station_x/y/z` + `instrument_height`.
    const px = scope.target_x, py = scope.target_y, pz = scope.target_z;
    const sx = scope.station_x, sy = scope.station_y;
    if (!Number.isFinite(px) || !Number.isFinite(py) ||
        !Number.isFinite(sx) || !Number.isFinite(sy)) return null;
    const dx = activeTarget.world_x - px;
    const dy = activeTarget.world_y - py;
    const { forward, right } = toSurveyorFrame(dx, dy, px, py, sx, sy);
    const distance_mm = Math.hypot(forward, right) * 1000;
    const dz_mm = ((activeTarget.actual_z ?? activeTarget.world_z ?? 0) - (pz ?? 0)) * 1000;
    return {
      forward_mm: forward * 1000,
      right_mm: right * 1000,
      dz_mm,
      distance_mm,
      onMark: distance_mm <= tolerance_mm,
    };
  }, [activeTarget, scope.target_x, scope.target_y, scope.target_z,
      scope.station_x, scope.station_y, tolerance_mm]);

  // Drive the beeper.
  useEffect(() => {
    if (stageName !== "target" || !guide) { setRate(null); return; }
    if (guide.onMark) { setRate(0); return; }        // continuous
    // Rate 60 ms at ≤2 mm → 900 ms at ≥50 cm.
    const d = Math.min(500, Math.max(2, guide.distance_mm));
    const rate = Math.round(60 + (d - 2) * (840 / 498));
    setRate(rate);
  }, [stageName, guide, setRate]);

  // ----- Stage confirm handlers -------------------------------------------
  const confirmPlane = async () => {
    if ((current.plane_shots || []).length < 3) {
      toast.error("Need 3+ plane shots"); return;
    }
    if (current.plane_mode !== "fitted") {
      setCurrent(await soApi.setPlaneMode(current.id, "fitted"));
    }
    setCaptured(false);
    resetStill();
    toast.success("Plane locked · move prism to Point A");
  };

  const confirmTarget = async () => {
    if (!activeTarget) return;
    try {
      const m = {
        x: scope.target_x, y: scope.target_y, z: scope.target_z,
        hz: scope.hz, v: scope.v, sd: scope.sd || 0,
      };
      const s = await soApi.captureActual(current.id, activeTarget.corner_id, {
        x: m.x, y: m.y, z: m.z, hz: m.hz, v: m.v, sd: m.sd,
        commanded_hz: activeTarget.hz, commanded_v: activeTarget.v,
      });
      // Also flip marked=true so the wizard advances.
      const s2 = await soApi.markTarget(current.id, activeTarget.corner_id, true);
      setCurrent(s2);
      toast.success(`${activeTarget.label} · marked · Δ ${s.targets.find((t) => t.corner_id === activeTarget.corner_id)?.verify_residual_mm?.toFixed(1) ?? "?"} mm`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Confirm failed");
    }
  };

  // ----- Render -----------------------------------------------------------

  return (
    <main className="mx-auto max-w-3xl p-4 sm:p-6" data-testid="setout-guided">
      <div className="mb-3 flex items-center justify-between">
        <button data-testid="guided-exit" onClick={onExit}
          className="rounded-md border border-[#2A2E39] bg-[#14161A] px-3 py-2 text-[10px] uppercase tracking-widest text-[#8B939A] hover:border-[#009E8E] hover:text-white">
          <ArrowLeft size={13} className="mr-1 inline" /> Steps view
        </button>
        <button data-testid="guided-mute" onClick={() => setMuted((v) => !v)}
          className="rounded-md border border-[#2A2E39] bg-[#14161A] px-3 py-2 text-[10px] uppercase tracking-widest text-[#8B939A]">
          {muted ? <><VolumeX size={13} className="mr-1 inline" /> muted</> : <><Volume2 size={13} className="mr-1 inline" /> sound</>}
        </button>
      </div>

      {stageName === "plane" && (
        <div className="rounded-md border border-[#F5A623]/40 bg-[#F5A623]/5 p-6 text-center" data-testid="stage-plane">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#F5A623]">Stage 1 · Plane</div>
          <h2 className="mt-1 text-3xl font-black text-white" style={{ fontFamily: "Chakra Petch, sans-serif" }}>
            {`Walk the prism across the surface — I'll record each spot when you stop`}
          </h2>
          <div className="mt-4 grid grid-cols-3 gap-2">
            <div className="rounded-sm border border-[#2A2E39] bg-[#0F1116] p-2">
              <div className="text-[9px] uppercase tracking-widest text-[#8B939A]">Shots</div>
              <div className="font-mono text-2xl text-[#F5A623]">{(current.plane_shots || []).length}</div>
              <div className="text-[9px] text-[#4A5054]">need 3+</div>
            </div>
            <div className="rounded-sm border border-[#2A2E39] bg-[#0F1116] p-2">
              <div className="text-[9px] uppercase tracking-widest text-[#8B939A]">Tilt</div>
              <div className="font-mono text-2xl text-[#8DC63F]">{current.plane_tilt_deg != null ? current.plane_tilt_deg.toFixed(1) + "°" : "—"}</div>
            </div>
            <div className="rounded-sm border border-[#2A2E39] bg-[#0F1116] p-2">
              <div className="text-[9px] uppercase tracking-widest text-[#8B939A]">RMS</div>
              <div className="font-mono text-2xl text-[#8DC63F]">{current.plane_rms_mm != null ? current.plane_rms_mm.toFixed(1) + " mm" : "—"}</div>
            </div>
          </div>
          <div className={`mt-4 rounded-md border-2 p-2 text-sm ${isStill && armed ? "border-[#8DC63F] bg-[#8DC63F]/10 text-[#8DC63F]" : "border-[#2A2E39] text-[#8B939A]"}`}>
            {busy ? "Recording…" : !armed ? "Move the prism to the first spot" : isStill ? "STILL · recording" : "Move to next spot"}
          </div>
          <button data-testid="guided-confirm-plane" onClick={confirmPlane} disabled={(current.plane_shots || []).length < 3}
            className="mt-4 w-full rounded-md border border-[#8DC63F] bg-[#8DC63F]/20 py-3 text-sm font-black uppercase tracking-widest text-[#8DC63F] disabled:opacity-30">
            <Check size={16} className="mr-1 inline" /> Lock plane · continue to Point A
          </button>
        </div>
      )}

      {(stageName === "anchor" || stageName === "orient") && (
        <div className="rounded-md border border-[#00E1C7]/40 bg-[#00E1C7]/5 p-6 text-center" data-testid={`stage-${stageName}`}>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#00E1C7]">
            {stageName === "anchor" ? "Stage 2 · Point A" : "Stage 3 · Point B direction"}
          </div>
          <h2 className="mt-1 text-3xl font-black text-white" style={{ fontFamily: "Chakra Petch, sans-serif" }}>
            {stageName === "anchor" ? "Move the prism to Point A and stop" : "Move the prism toward Point B and stop"}
          </h2>
          <div className={`mt-6 rounded-md border-2 p-4 text-lg ${isStill && armed ? "border-[#8DC63F] bg-[#8DC63F]/10 text-[#8DC63F]" : "border-[#2A2E39] text-[#8B939A]"}`}>
            {busy ? "Recording…" : !armed ? "Start moving the prism…" : isStill ? "STILL · recording" : "Waiting for you to stop"}
          </div>
        </div>
      )}

      {stageName === "target" && activeTarget && guide && (
        <div className="rounded-md border border-[#8DC63F]/40 bg-[#8DC63F]/5 p-4" data-testid="stage-target">
          <div className="mb-3 flex items-center justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#8DC63F]">Guide to {activeTarget.label}</div>
              <div className="font-mono text-lg text-white">
                {guide.distance_mm.toFixed(0)} mm · Δz {guide.dz_mm.toFixed(0)} mm
              </div>
            </div>
            <div className={`rounded-md px-3 py-2 font-black uppercase tracking-widest ${guide.onMark ? "bg-[#8DC63F] text-[#0A0B0E] animate-pulse" : "bg-[#2A2E39] text-[#8B939A]"}`}>
              {guide.onMark ? "· ON MARK ·" : "walk"}
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div />
            <BigArrow dir={guide.forward_mm >= 0 ? "up" : "down"} magnitudeMm={Math.abs(guide.forward_mm)} label={guide.forward_mm >= 0 ? "away from TS" : "toward TS"} />
            <div />
            <BigArrow dir="left" magnitudeMm={guide.right_mm < 0 ? Math.abs(guide.right_mm) : 0} label="left" />
            <div className="flex items-center justify-center rounded-md border border-dashed border-[#2A2E39] bg-[#0A0B0E] p-4 text-center text-[10px] uppercase tracking-widest text-[#4A5054]">
              You<br/>(facing away<br/>from TS)
            </div>
            <BigArrow dir="right" magnitudeMm={guide.right_mm > 0 ? guide.right_mm : 0} label="right" />
          </div>
          <button data-testid="guided-confirm-target" onClick={confirmTarget} disabled={!guide.onMark}
            className="mt-4 w-full rounded-md border border-[#8DC63F] bg-[#8DC63F]/20 py-3 text-sm font-black uppercase tracking-widest text-[#8DC63F] disabled:opacity-30">
            <Check size={16} className="mr-1 inline" /> Confirm {activeTarget.label} marked
          </button>
        </div>
      )}

      {stageName === "complete" && (
        <div className="rounded-md border border-[#8DC63F] bg-[#8DC63F]/10 p-8 text-center" data-testid="stage-complete">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#8DC63F]">Complete</div>
          <h2 className="mt-2 text-3xl font-black text-[#8DC63F]" style={{ fontFamily: "Chakra Petch, sans-serif" }}>
            All corners marked
          </h2>
          <button onClick={onExit} className="mt-6 rounded-md border border-[#8DC63F] bg-[#8DC63F]/20 px-6 py-3 text-sm font-black uppercase tracking-widest text-[#8DC63F]">
            Back to steps view
          </button>
        </div>
      )}
    </main>
  );
}
